import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { RiArrowRightSLine } from "@remixicon/react";
import { tokens } from "../../utils/figmaTokens";

// A panel card inside the white page card (Sessions overview and Notes on 10697:16585,
// Wallet on 10791:50724): sunken fill, 24 px padding, an icon and a title, an optional
// caption line under the title, and an optional foot. The tutor page's cards (047 step 4) set
// their own padding and fill through `sx`, and their caption's type through `subtitleVariant`.
export const SectionCard = ({ title, Icon, subtitle, subtitleVariant = "labelMedium", action, footer, children, sx }) => (
  <Box
    sx={{
      bgcolor: tokens.surface.sunken,
      border: `1px solid ${tokens.border.default}`,
      borderRadius: "16px",
      p: 3,
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      minWidth: 0,
      ...sx,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
      {Icon && (
        <Box sx={{ display: "flex", color: tokens.text.primary }}>
          <Icon size={24} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography variant="titleSection">{title}</Typography>
        {subtitle && (
          <Typography variant={subtitleVariant} component="div" sx={{ color: tokens.text.tertiary }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
    {footer}
  </Box>
);

// The table card (Admin · Table 10601:1053): white, a hairline border, no padding of its
// own — the head, the toolbar, the table and the pager each carry theirs. A list page's table
// has no head (Users table 10620:2936): it leaves out `title` and `action`. The toolbar sits on
// the Table Toolbar's 3 px line (10600:635), drawn inside so the toolbar stays 68 px tall.
export const TableCard = ({ title, subtitle, action, toolbar, footer, children }) => (
  <Box sx={{ bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.subtle}`, borderRadius: "16px", overflow: "hidden", minWidth: 0 }}>
    {(title || action) && (
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", p: "18px 16px 14px" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="titleSection">{title}</Typography>
          {subtitle && (
            <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {toolbar && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          p: "14px 16px",
          boxShadow: `inset 0 -3px 0 ${tokens.border.default}`,
        }}
      >
        {toolbar}
      </Box>
    )}
    {children}
    {footer}
  </Box>
);

// "View all ›" at the foot of a panel card (Notes and Activity log feet, 10708:9047).
export const ViewAllLink = ({ to, children = "View all" }) => (
  <Box sx={{ borderTop: `1px solid ${tokens.border.default}`, pt: "10px" }}>
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: tokens.brand.text, fontSize: 14, lineHeight: "22px", fontWeight: 500 }}
    >
      {children}
      <RiArrowRightSLine size={14} />
    </Link>
  </Box>
);
